import type {
  KeyImprovement,
  KeyImprovementsSection,
  MissedOpportunitiesSection,
  MissedOpportunity,
  RedFlag,
  RedFlagsSection,
  ScorecardItem,
  SessionActionsSection,
  ThingDoneWell,
  ThingsDoneWellSection,
  XmlItemRating,
} from "../../../shared/analysis-types";
import {
  getSessionActionCategoryLabel,
  scorecardRatingDisplay,
} from "./analysis-display";

export interface CopyFormat {
  html: string;
  text: string;
}

export interface ScorecardCopyItem extends Omit<ScorecardItem, "id"> {
  label: string;
}

const colors = {
  bark: "#28030F",
  interactive: "#2563EB",
  sand100: "#F7F5F2",
  sand200: "#EDE9E3",
  sand500: "#7A7266",
  forest500: "#166534",
};

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function paragraph(value: string): string {
  return `<p>${escapeHtml(value)}</p>`;
}

function heading(value: string, level: 2 | 3 = 2): string {
  return `<h${level}>${escapeHtml(value)}</h${level}>`;
}

function labeledText(label: string, value?: string): string[] {
  return value ? [`${label}: ${value}`] : [];
}

function labeledHtml(label: string, value?: string): string {
  if (!value) return "";
  return `<p><strong>${escapeHtml(label)}:</strong> ${escapeHtml(value)}</p>`;
}

function joinBlocks(blocks: string[]): string {
  return blocks.filter(Boolean).join("\n\n");
}

/**
 * Generic single-blob section formatter kept for compatibility with earlier
 * tanstack call sites. Prefer the per-section formatters below, which match
 * the legacy rich-copy output.
 */
export function formatAnalysisSection(title: string, text: string): CopyFormat {
  return {
    text: `${title}\n\n${text}`,
    html: [heading(title), `<p>${escapeHtml(text).replace(/\n/g, "<br />")}</p>`].join(""),
  };
}

function formatTableHeader(value: string, width: string): string {
  return `<th style="width:${width};border:1px solid ${colors.sand200};background-color:${colors.sand100};color:${colors.sand500};padding:8px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;">${escapeHtml(value)}</th>`;
}

function formatTableCell(value: string, options?: { width?: string; bold?: boolean }): string {
  const width = options?.width ? `width:${options.width};` : "";
  const weight = options?.bold ? "font-weight:700;" : "";
  return `<td style="${width}border:1px solid ${colors.sand200};padding:8px;vertical-align:top;color:${colors.bark};${weight}">${escapeHtml(value)}</td>`;
}

function formatTrustedHtmlTableCell(
  html: string,
  options?: { width?: string; bold?: boolean },
): string {
  const width = options?.width ? `width:${options.width};` : "";
  const weight = options?.bold ? "font-weight:700;" : "";
  return `<td style="${width}border:1px solid ${colors.sand200};padding:8px;vertical-align:top;color:${colors.bark};${weight}">${html}</td>`;
}

function formatRatingCell(rating: XmlItemRating): string {
  const { copyStyle, label } = scorecardRatingDisplay[rating];
  const border = copyStyle.badgeBorder ?? "1px solid transparent";
  const badge = `<span style="display:inline-block;border-radius:999px;border:${border};background-color:${copyStyle.badgeBackground};color:${copyStyle.badgeColor};padding:3px 10px;font-size:12px;font-weight:700;white-space:nowrap;">${escapeHtml(label)}</span>`;
  return formatTrustedHtmlTableCell(badge, { width: "16%" });
}

function detailLineHtml(
  label: string,
  value: string | undefined,
  color: string,
): string {
  if (!value) return "";

  return `<div style="margin-top:6px;"><strong style="color:${color};font-size:11px;font-weight:700;text-transform:uppercase;">${escapeHtml(label)}:</strong> <span style="color:${colors.bark};">${escapeHtml(value)}</span></div>`;
}

function formatScorecardDetailsHtml(item: ScorecardCopyItem): string {
  return [
    `<div style="color:${colors.bark};">${escapeHtml(item.summary)}</div>`,
    detailLineHtml("Evidence", item.evidence, colors.sand500),
    detailLineHtml("What to try", item.what_to_try, colors.interactive),
    detailLineHtml("Why it matters", item.why_it_matters, colors.sand500),
    detailLineHtml(
      "Emphasize for reinforcement",
      item.copy_paste_reinforcement,
      colors.forest500,
    ),
  ]
    .filter(Boolean)
    .join("");
}

function formatScorecardDetails(item: ScorecardCopyItem): string {
  return [
    item.summary,
    ...labeledText("Evidence", item.evidence),
    ...labeledText("What to try", item.what_to_try),
    ...labeledText("Why it matters", item.why_it_matters),
    ...labeledText(
      "Emphasize for reinforcement",
      item.copy_paste_reinforcement,
    ),
  ]
    .filter(Boolean)
    .join("\n");
}

function formatScorecardTable(items: ScorecardCopyItem[]): string {
  const headers = [
    formatTableHeader("Item", "28%"),
    formatTableHeader("Rating", "16%"),
    formatTableHeader("Details / Examples", "56%"),
  ].join("");

  const rows = items
    .map((item) => {
      const rowStyle = `background-color:${scorecardRatingDisplay[item.rating].copyStyle.rowBackground};`;
      const cells = [
        formatTableCell(item.label, { width: "28%", bold: true }),
        formatRatingCell(item.rating),
        formatTrustedHtmlTableCell(formatScorecardDetailsHtml(item), { width: "56%" }),
      ].join("");

      return `<tr style="${rowStyle}">${cells}</tr>`;
    })
    .join("");

  return `<table style="border-collapse:collapse;width:100%;font-family:Inter,Arial,sans-serif;font-size:13px;color:${colors.bark};"><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
}

function formatScorecardTextRows(
  items: ScorecardCopyItem[],
): string[] {
  return items.map((item) =>
    [
      item.label,
      `Rating: ${scorecardRatingDisplay[item.rating].label}`,
      formatScorecardDetails(item),
    ]
      .filter(Boolean)
      .join("\n"),
  );
}

export function formatScorecardSectionCopy(
  title: string,
  items: ScorecardCopyItem[],
): CopyFormat {
  return {
    html: [heading(title), formatScorecardTable(items)].join("\n"),
    text: joinBlocks([title, ...formatScorecardTextRows(items)]),
  };
}

export function formatScorecardItemCopy(
  item: ScorecardCopyItem,
): CopyFormat {
  return {
    html: formatScorecardTable([item]),
    text: formatScorecardTextRows([item]).join("\n"),
  };
}

export function formatSessionActionsCopy(
  data: SessionActionsSection,
): CopyFormat {
  const actionHtml = data.actions
    .map((action) =>
      labeledHtml(getSessionActionCategoryLabel(action.category), action.text),
    )
    .join("\n");
  const retentionHtml = data.retention
    ? labeledHtml(`Retention (${data.retention.status})`, data.retention.text)
    : "";
  const text = [
    "Session Actions",
    ...data.actions.map(
      (action) =>
        `${getSessionActionCategoryLabel(action.category)}: ${action.text}`,
    ),
    data.retention
      ? `Retention (${data.retention.status}): ${data.retention.text}`
      : "",
  ].filter(Boolean);

  return {
    html: [heading("Session Actions"), actionHtml, retentionHtml].join("\n"),
    text: text.join("\n"),
  };
}

export function formatMissedOpportunityCopy(
  opportunity: MissedOpportunity,
): CopyFormat {
  return {
    html: [
      heading("Missed Opportunity", 3),
      labeledHtml("Client quote", opportunity.client_quote),
      labeledHtml("What happened", opportunity.what_happened),
      labeledHtml("What to try", opportunity.what_to_try),
      labeledHtml("Why it matters", opportunity.why_it_matters),
    ].join("\n"),
    text: [
      "Missed Opportunity",
      `Client quote: ${opportunity.client_quote}`,
      `What happened: ${opportunity.what_happened}`,
      `What to try: ${opportunity.what_to_try}`,
      `Why it matters: ${opportunity.why_it_matters}`,
    ].join("\n"),
  };
}

export function formatMissedOpportunitiesCopy(
  data: MissedOpportunitiesSection,
): CopyFormat {
  return joinCopyFormats(
    "Missed Opportunities",
    data.opportunities.map(formatMissedOpportunityCopy),
  );
}

export function formatThingDoneWellCopy(observation: ThingDoneWell): CopyFormat {
  return {
    html: [
      heading(observation.lede, 3),
      labeledHtml("Source quote", observation.rd_quote),
      paragraph(observation.body),
      labeledHtml("Skill refs", observation.skill_refs.join(", ")),
    ].join("\n"),
    text: [
      observation.lede,
      ...labeledText("Source quote", observation.rd_quote),
      observation.body,
      ...labeledText("Skill refs", observation.skill_refs.join(", ")),
    ].join("\n"),
  };
}

export function formatThingsDoneWellCopy(
  data: ThingsDoneWellSection,
): CopyFormat {
  return joinCopyFormats(
    "Things Done Well",
    data.observations.map(formatThingDoneWellCopy),
  );
}

export function formatKeyImprovementCopy(
  improvement: KeyImprovement,
): CopyFormat {
  return {
    html: [
      heading(improvement.lede, 3),
      labeledHtml("Where", improvement.where),
      labeledHtml("Why it matters", improvement.why_it_matters),
      labeledHtml("Try this", improvement.try_this),
    ].join("\n"),
    text: [
      improvement.lede,
      `Where: ${improvement.where}`,
      `Why it matters: ${improvement.why_it_matters}`,
      `Try this: ${improvement.try_this}`,
    ].join("\n"),
  };
}

export function formatKeyImprovementsCopy(
  data: KeyImprovementsSection,
): CopyFormat {
  return joinCopyFormats(
    "Key Improvements",
    data.improvements.map(formatKeyImprovementCopy),
  );
}

export function formatRedFlagCopy(flag: RedFlag): CopyFormat {
  const category = flag.category.charAt(0).toUpperCase() + flag.category.slice(1);
  return {
    html: [
      heading(flag.lede, 3),
      labeledHtml("Category", category),
      labeledHtml("Severity", flag.severity),
      paragraph(flag.body),
    ].join("\n"),
    text: [
      flag.lede,
      `Category: ${category}`,
      `Severity: ${flag.severity}`,
      flag.body,
    ].join("\n"),
  };
}

export function formatRedFlagsCopy(data: RedFlagsSection): CopyFormat {
  if (data.flags.length === 0) {
    return {
      html: [heading("Red Flags"), paragraph("No red flags this session.")].join("\n"),
      text: "Red Flags\n\nNo red flags this session.",
    };
  }

  return joinCopyFormats("Red Flags", data.flags.map(formatRedFlagCopy));
}

function joinCopyFormats(title: string, formats: CopyFormat[]): CopyFormat {
  return {
    html: [heading(title), ...formats.map((format) => format.html)].join("\n"),
    text: joinBlocks([title, ...formats.map((format) => format.text)]),
  };
}
